import {
  AlertAction,
  AlertCancel,
  AlertContent,
  AlertDescription,
  AlertOverlay,
  AlertPortal,
  AlertRoot,
  AlertTitle,
  BodyText,
  Button,
  Field,
  FieldError,
  FieldLabel,
  MultiSelectSearch,
} from "@/components/ui";
import type {
  CommandListItemVariant,
  ItemOptionProps,
} from "@/components/ui/Search/types";
import { TRACKERS } from "@/constants/Trackers";
import { useMedHistoryConditionSearch } from "@/features/Charting/network/useMedHistoryConditionSearch";
import { trackEvent } from "@/lib/tracking";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { constants } from "@curai/js-fhir-tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { SearchIcon } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDebounce } from "react-use";
import type { z } from "zod";
import { SubmitCancelButtons } from "../../components/SubmitCancelButtons";
import { medicalHistoryMultiFormSchema } from "../schemas";

interface AddMultipleMedicalHistoryItemsProps {
  patientId: string;
  onCancel?: () => void;
  onSubmit: (
    conditionCodes: Array<{
      coding: Array<{ code: string; display: string; system: string }>;
      text: string;
    }>,
  ) => void;
  isSubmitting: boolean;
}

export const AddMultipleMedicalHistoryItems = ({
  patientId,
  onCancel,
  onSubmit,
  isSubmitting,
}: AddMultipleMedicalHistoryItemsProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [duplicateWarningItem, setDuplicateWarningItem] = useState<
    ItemOptionProps | undefined
  >(undefined);

  useDebounce(
    () => {
      setDebouncedSearchTerm(searchTerm);
    },
    200,
    [searchTerm],
  );

  const { data: conditions, isFetching: isFetchingConditions } =
    useMedHistoryConditionSearch({
      searchTerm: debouncedSearchTerm,
      patientId,
    });
  const { data: isSingleEncounterVisitsEnabled } = useConfigCatFeatureFlag(
    "single-encounter-visits",
  );

  const form = useForm<z.infer<typeof medicalHistoryMultiFormSchema>>({
    resolver: zodResolver(medicalHistoryMultiFormSchema),
    defaultValues: {
      conditionCodes: [],
    },
  });

  const handleItemAdded = (item: ItemOptionProps) => {
    const metadata = item.metadata as { isDuplicate?: boolean };
    if (metadata.isDuplicate === true) {
      setDuplicateWarningItem(item);
      return;
    }

    const currentConditionCodes = form.getValues("conditionCodes");
    const newCondition = {
      conditionCode: item.value as string,
      conditionCodeDisplay: item.label,
    };

    // Check if condition already exists in the form
    const exists = currentConditionCodes.some(
      (condition) => condition.conditionCode === newCondition.conditionCode,
    );

    if (!exists) {
      const newConditionCodes = [...currentConditionCodes, newCondition];
      form.setValue("conditionCodes", newConditionCodes, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const handleItemRemoved = (item: { value: string | number }) => {
    const currentConditionCodes = form.getValues("conditionCodes");
    // Extract the condition code from the value (format: "code-index")
    const conditionCode = item.value.toString().split("-")[0];
    const newConditionCodes = currentConditionCodes.filter(
      (condition) => condition.conditionCode !== conditionCode,
    );
    form.setValue("conditionCodes", newConditionCodes, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSubmit = (
    data: z.infer<typeof medicalHistoryMultiFormSchema>,
  ) => {
    trackEvent(TRACKERS.ADD_EOC);

    const conditionCodes = data.conditionCodes.map((condition) => ({
      coding: [
        {
          code: condition.conditionCode,
          display: condition.conditionCodeDisplay,
          system: constants.SNOMED_SYSTEM_URL,
        },
      ],
      text: condition.conditionCodeDisplay,
    }));

    onSubmit(conditionCodes);
  };

  const searchResults =
    conditions?.map((code) => {
      // With single-encounter visits on, the medical history shows only problem
      // list items, so only those count as duplicates.
      const isDuplicate =
        isSingleEncounterVisitsEnabled === true
          ? Boolean(code.existingConditionId)
          : Boolean(code.existingEpisodeOfCareId);
      return {
        label: code.conditionDisplay,
        value: code.conditionCode,
        endLabel: isDuplicate ? "Duplicate" : undefined,
        variant: (isDuplicate
          ? "warning"
          : "default") as CommandListItemVariant,
        metadata: {
          system: code.conditionSystem,
          display: code.conditionDisplay,
          isDuplicate,
          originalCode: code.conditionCode,
        },
      };
    }) || [];

  return (
    <div>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-2"
      >
        <BodyText size="sm" bold>
          Add Medical History
        </BodyText>

        <Controller
          control={form.control}
          name="conditionCodes"
          render={({ field, fieldState }) => (
            <Field>
              <FieldLabel htmlFor="conditionCodes">Conditions</FieldLabel>
              {fieldState.error && (
                <BodyText size="sm" textColor="error">
                  {fieldState.error.message}
                </BodyText>
              )}
              <MultiSelectSearch
                placeholder="Search for conditions"
                selectedItems={field.value?.map((condition, index) => ({
                  label: condition.conditionCodeDisplay,
                  shortenedLabel: condition.conditionCodeDisplay,
                  value: `${condition.conditionCode}-${index}`,
                  metadata: {
                    system: constants.SNOMED_SYSTEM_URL,
                    display: condition.conditionCodeDisplay,
                  },
                }))}
                noResultsText="No conditions found"
                options={searchResults}
                onInputChange={(text) => {
                  setSearchTerm(text);
                }}
                onItemAdded={handleItemAdded}
                onItemRemoved={handleItemRemoved}
                disabled={false}
                minLength={2}
                isLoading={isFetchingConditions}
                showLoadingSpinner={true}
                openOnFocus
                autoFocusInput
                autoFocusInputDelay={100}
                leftIcon={SearchIcon}
              />
              <FieldError errors={[fieldState.error]} />
            </Field>
          )}
        />

        <SubmitCancelButtons
          isSaving={isSubmitting}
          onCancel={onCancel}
          savingDisabled={form.watch("conditionCodes").length === 0}
        />
      </form>

      <AlertRoot
        isControlled
        isOpen={Boolean(duplicateWarningItem)}
        onOpenChange={() => {}}
      >
        <AlertPortal>
          <AlertOverlay />
          <AlertContent>
            <AlertTitle>Duplicate Condition</AlertTitle>
            <AlertDescription className="text-body-sm text-primary">
              {duplicateWarningItem?.label} has been previously charted. Would
              you like to add a duplicate entry?
            </AlertDescription>
            <div className="flex gap-4 align-middle justify-end">
              <AlertCancel>
                <Button
                  onClick={() => {
                    setDuplicateWarningItem(undefined);
                  }}
                  size="sm"
                  variant="secondary"
                >
                  No
                </Button>
              </AlertCancel>
              <AlertAction>
                <Button
                  onClick={() => {
                    // Add the duplicate condition
                    const currentConditionCodes =
                      form.getValues("conditionCodes");
                    const newCondition = {
                      conditionCode: duplicateWarningItem?.value as string,
                      conditionCodeDisplay: duplicateWarningItem?.label || "",
                    };
                    const newConditionCodes = [
                      ...currentConditionCodes,
                      newCondition,
                    ];
                    form.setValue("conditionCodes", newConditionCodes, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    setDuplicateWarningItem(undefined);
                  }}
                  size="sm"
                  variant="primary"
                >
                  Yes
                </Button>
              </AlertAction>
            </div>
          </AlertContent>
        </AlertPortal>
      </AlertRoot>
    </div>
  );
};
